import { Edit as EditIcon } from '@mui/icons-material';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingEntity } from '@/types/api';

import { useSetSettings } from '../api/set-setting';

import { SettingForm } from './setting-form';

interface EditSettingProps {
  setting: SettingEntity;
}

const editSettingPermissions = [permission.settings.update];

export const EditSetting = ({ setting }: EditSettingProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { orgId } = useScopeIds('org');
  const { projectId } = useScopeIds();
  const { mutate: setSetting, isPending } = useSetSettings();

  const handleSubmit = (value: unknown) => {
    setSetting(
      {
        key: setting.key,
        value,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      },
    );
  };

  const subject = useMemo(
    () => ({ orgId, projectId, key: setting.key }),
    [orgId, projectId, setting.key],
  );

  return (
    <>
      <OidcProtectedComponent requireAll={editSettingPermissions} subject={subject}>
        <Tooltip title={t('common.edit')}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setIsOpen(true);
            }}
            sx={{ p: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </OidcProtectedComponent>

      <Dialog
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('settings.edit.title', { key: setting.key })}</DialogTitle>
        <DialogContent>
          <SettingForm
            setting={setting}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsOpen(false);
            }}
            isSubmitting={isPending}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
